import { useTranslation } from 'react-i18next';
import type { DateScoringMode } from '../api.js';
import styles from './RadioWithHelp.module.css';

/** In the order they are offered, cheapest commitment first. */
export const DATE_SCORING_MODES: DateScoringMode[] = [
  'placement',
  'ladder',
  'closeness',
  'winner_takes_all',
];

/**
 * The label and explanation for each scoring mode, resolved once here so the
 * creation editor and the later rules editor can never describe the same mode
 * differently.
 */
export function useDateScoringText(): Record<DateScoringMode, { label: string; help: string }> {
  const { t } = useTranslation();

  return {
    placement: {
      label: t('customCompetition.dateScoringPlacement'),
      help: t('customCompetition.dateScoringPlacementHelp'),
    },
    ladder: {
      label: t('customCompetition.dateScoringLadder'),
      help: t('customCompetition.dateScoringLadderHelp'),
    },
    closeness: {
      label: t('customCompetition.dateScoringCloseness'),
      help: t('customCompetition.dateScoringClosenessHelp'),
    },
    winner_takes_all: {
      label: t('customCompetition.dateScoringWinnerTakesAll'),
      help: t('customCompetition.dateScoringWinnerTakesAllHelp'),
    },
  };
}

/**
 * A radio option with its own "?" toggle revealing how that choice works.
 *
 * The four `date` scoring modes are why this exists: labels like "Points
 * ladder by placement" are short enough to scan but genuinely don't explain
 * themselves, and four permanently-visible paragraphs would bury the choice
 * they describe. So each carries its own expandable explanation.
 *
 * Its own component with its own stylesheet — rather than JSX repeated per
 * screen — because the same four options are configured in two places (the
 * creation editor and `EditQuestionResultsScreen`), and an organizer editing a
 * question afterwards needs the same explanation they had when creating it.
 * Duplicating it is how one copy silently loses the help buttons, which is
 * exactly what happened before this was extracted.
 */
export default function RadioWithHelp({
  name,
  checked,
  label,
  help,
  helpOpen,
  onSelect,
  onToggleHelp,
}: {
  /** Shared across the group, so the browser treats them as one choice. */
  name: string;
  checked: boolean;
  label: string;
  help: string;
  helpOpen: boolean;
  onSelect: () => void;
  onToggleHelp: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <label className={styles.radioOption}>
        <input type="radio" name={name} checked={checked} onChange={onSelect} />
        {label}
        <button
          type="button"
          className={styles.helpButton}
          aria-label={t('customCompetition.dateScoringHelpLabel', { mode: label })}
          aria-expanded={helpOpen}
          onClick={(e) => {
            // Inside a <label>, so a bare click would also select the radio.
            // Asking what an option means must never commit to it.
            e.preventDefault();
            onToggleHelp();
          }}
        >
          ?
        </button>
      </label>
      {helpOpen ? <p className={styles.helpText}>{help}</p> : null}
    </div>
  );
}
